import type { Vec2 } from '../../geo/types';
import { distancePointSegment, pointInRing } from '../../geo/polygon';
import type { Lot } from './types';
import type { PartyWalls } from './partyWalls';
import { lotFrame, lotSides, type LotSide } from './lotFrame';
import { SHORE } from './frontSources';
import { createCellGrid, type CellGrid } from './cellGrid';

export type StreetKind = 'street' | 'alley' | 'quay' | 'dike' | 'road' | 'path' | 'square' | 'shore';

/** A street, quay, square or stretch of IJ shore that a wall of a house faces. */
export interface StreetFront {
  readonly id: string;
  readonly kind: StreetKind;
  /** Between the house fronts on its two sides, in metres; a square has none. */
  readonly width: number | null;
}

/** Where another street ends on a street, in metres along the street's centreline from its start. */
export interface Junction {
  readonly id: string;
  readonly along: number;
}

/** What a house's front faces, and each of its free side walls where one stands on a street: a corner. */
export interface HouseStreets {
  readonly front: StreetFront | null;
  readonly left: StreetFront | null;
  readonly right: StreetFront | null;
  /** The front's middle along its street's centreline, from the line's start; none on a square or the shore. */
  readonly along: number | null;
  /** Where other streets end on the front street, so a house can tell which stretch of it it stands on. */
  readonly junctions: readonly Junction[];
}

export const NO_STREETS: HouseStreets = { front: null, left: null, right: null, along: null, junctions: [] };

type MapStreet = StreetMap['streets'][number];

/** The layout's streets and squares, as far as a house needs them. */
export interface StreetMap {
  readonly streets: readonly {
    readonly id: string;
    readonly kind: Exclude<StreetKind, 'square' | 'shore'>;
    readonly width: number;
    readonly centerline: readonly Vec2[];
  }[];
  readonly squares: readonly { readonly id: string; readonly polygon: readonly Vec2[] }[];
}

/**
 * Where a free side wall is looked past for a street: this far back from the front corner and this
 * far out from the wall, so a probe lands in a crossing street however its edge meets the lot.
 */
const PROBE = { back: [1.0, 2.5], out: [0.75, 1.5], endMargin: 0.5 } as const;

/* A street ends on another when its end lies within the other's half-width and this much more. */
const JUNCTION_REACH = 1.5;

/* About a lot's depth: a probe meets the few segments filed in its cell. */
const INDEX_CELL = 16;

interface Piece {
  /** The street's place in the map, so the first street in map order wins wherever two overlap. */
  readonly street: number;
  readonly a: Vec2;
  readonly b: Vec2;
}

/* A lot's source id is its source's name and a running number, as `street-kalverstraat-2`. */
function sourceName(id: string): string {
  return id.replace(/-\d+$/, '');
}

function frontOf(lot: Lot, street: MapStreet | undefined, map: StreetMap): StreetFront | null {
  if (street) return { id: street.id, kind: street.kind, width: street.width };
  if (map.squares.some((s) => s.id === lot.sourceId)) return { id: lot.sourceId, kind: 'square', width: null };
  if (lot.sourceId.startsWith('shore:')) return { id: lot.sourceId, kind: 'shore', width: SHORE.bank };
  return null;
}

function probes(side: LotSide, outward: Vec2, depth: number): Vec2[] {
  const along: Vec2 = [(side.to[0] - side.from[0]) / depth, (side.to[1] - side.from[1]) / depth];
  return PROBE.back
    .map((b) => Math.min(b, depth - PROBE.endMargin))
    .flatMap((b) =>
      PROBE.out.map((o): Vec2 => [
        side.from[0] + along[0] * b + outward[0] * o,
        side.from[1] + along[1] * b + outward[1] * o,
      ])
    );
}


/** Distance from a line to a point, and how far along the line the nearest point lies. */
function project(line: readonly Vec2[], p: Vec2): { readonly distance: number; readonly along: number } {
  let best = { distance: Infinity, along: 0 };
  let walked = 0;
  for (let i = 1; i < line.length; i++) {
    const [a, b] = [line[i - 1]!, line[i]!];
    const length = Math.hypot(b[0] - a[0], b[1] - a[1]);
    const t = length > 0 ? Math.max(0, Math.min(1, ((p[0] - a[0]) * (b[0] - a[0]) + (p[1] - a[1]) * (b[1] - a[1])) / length ** 2)) : 0;
    const distance = Math.hypot(p[0] - a[0] - t * (b[0] - a[0]), p[1] - a[1] - t * (b[1] - a[1]));
    if (distance < best.distance) best = { distance, along: walked + t * length };
    walked += length;
  }
  return best;
}

/* Whether a point lies within `reach` of a line's bounding box, before the line is walked for it. */
function nearBox(line: readonly Vec2[], reach: number): (p: Vec2) => boolean {
  const xs = line.map((q) => q[0]);
  const ys = line.map((q) => q[1]);
  const [x0, x1, y0, y1] = [Math.min(...xs) - reach, Math.max(...xs) + reach, Math.min(...ys) - reach, Math.max(...ys) + reach];
  return (p) => p[0] >= x0 && p[0] <= x1 && p[1] >= y0 && p[1] <= y1;
}

function junctionsOf(street: MapStreet, map: StreetMap): Junction[] {
  const own = sourceName(street.id);
  const near = nearBox(street.centerline, street.width / 2 + JUNCTION_REACH);
  return map.streets.flatMap((other) => {
    if (sourceName(other.id) === own) return [];
    const candidates = [other.centerline[0]!, other.centerline[other.centerline.length - 1]!].filter(near);
    const ends = candidates.map((e) => project(street.centerline, e));
    const within = ends.filter((e) => e.distance <= street.width / 2 + JUNCTION_REACH).sort((a, b) => a.distance - b.distance);
    return within.length > 0 ? [{ id: other.id, along: within[0]!.along }] : [];
  });
}

/** Every segment of every street, filed under the cells its half-width reaches. */
function indexStreets(map: StreetMap): CellGrid<Piece> {
  const grid = createCellGrid<Piece>(INDEX_CELL);
  map.streets.forEach((s, street) => {
    const half = s.width / 2;
    for (let i = 1; i < s.centerline.length; i++) {
      const [a, b] = [s.centerline[i - 1]!, s.centerline[i]!];
      grid.insert(
        { street, a, b },
        {
          minX: Math.min(a[0], b[0]) - half,
          minY: Math.min(a[1], b[1]) - half,
          maxX: Math.max(a[0], b[0]) + half,
          maxY: Math.max(a[1], b[1]) + half,
        }
      );
    }
  });
  return grid;
}

interface Streets {
  readonly map: StreetMap;
  readonly grid: CellGrid<Piece>;
}

/** The street, other than the lot's own under any number, or the square that a point stands in. */
function streetAt(p: Vec2, own: string, { map, grid }: Streets): StreetFront | null {
  let first = Infinity;
  for (const piece of grid.at(p[0], p[1])) {
    const s = map.streets[piece.street]!;
    if (piece.street < first && sourceName(s.id) !== own && distancePointSegment(p, piece) <= s.width / 2) {
      first = piece.street;
    }
  }
  const street = map.streets[first];
  if (street) return { id: street.id, kind: street.kind, width: street.width };
  const square = map.squares.find((s) => s.id !== own && pointInRing(p, s.polygon));
  return square ? { id: square.id, kind: 'square', width: null } : null;
}

function sideStreet(lot: Lot, which: 'left' | 'right', streets: Streets): StreetFront | null {
  const { right, facing, depth, shear = 0 } = lotFrame(lot);
  const sign = which === 'left' ? -1 : 1;
  const scale = Math.hypot(1, shear);
  const outward: Vec2 = [
    (right[0] + facing[0] * shear) * sign / scale,
    (right[1] + facing[1] * shear) * sign / scale,
  ];
  const own = sourceName(lot.sourceId);
  for (const p of probes(lotSides(lot)[which], outward, depth)) {
    const found = streetAt(p, own, streets);
    if (found) return found;
  }
  return null;
}

/**
 * For each lot, the street its front faces and the streets its free side walls face. A back
 * building faces its courtyard, so it has none; a side wall a neighbour shares faces nothing.
 */
export function houseStreetsOf(lots: readonly Lot[], party: readonly PartyWalls[], map: StreetMap): HouseStreets[] {
  const streets: Streets = { map, grid: indexStreets(map) };
  const byId = new Map(map.streets.map((s) => [s.id, s]));
  const junctions = new Map<string, Junction[]>();
  const junctionsFor = (street: MapStreet): Junction[] => {
    const known = junctions.get(street.id) ?? junctionsOf(street, map);
    junctions.set(street.id, known);
    return known;
  };
  return lots.map((lot, i) => {
    if (lot.kind !== 'house') return NO_STREETS;
    const shared = party[i]!;
    const street = byId.get(lot.sourceId);
    return {
      front: frontOf(lot, street, map),
      left: shared.left ? null : sideStreet(lot, 'left', streets),
      right: shared.right ? null : sideStreet(lot, 'right', streets),
      along: street ? project(street.centerline, lotFrame(lot).frontMid).along : null,
      junctions: street ? junctionsFor(street) : [],
    };
  });
}
